import { useState, useEffect, useMemo } from "react";
import { useContactDetailModalTabs } from "@/lib/hooks/entities/useContactDetailModalTabs";
import { useTicketDetailModalTabs } from "@/lib/hooks/entities/useTicketDetailModalTabs";
import { useTaskDetailModalTabs } from "@/lib/hooks/entities/useTaskDetailModalTabs";
import { useCompanyDetailModalTabs } from "@/lib/hooks/entities/useCompanyDetailModalTabs";
import { useTimeEntryDetailModalTabs } from "@/lib/hooks/entities/useTimeEntryDetailModalTabs";
import { useDealDetailModalTabs } from "@/lib/hooks/entities/useDealDetailModalTabs";
import { useCommunicationsDetailModalTabs } from "@/lib/hooks/entities/useCommunicationsDetailModalTabs";
import { useOfferingDetailModalTabs } from "@/lib/hooks/entities/useOfferingDetailModalTabs";
import { useRecurringTransactionDetailModalTabs } from "@/lib/hooks/entities/useRecurringTransactionDetailModalTabs";
import { useContractDetailModalTabs } from "@/lib/hooks/entities/useContractDetailModalTabs";
import { useAdminUserDetailModalTabs } from "@/lib/hooks/entities/useAdminUserDetailModalTabs";
import type { BaseEntity } from "../../UniversalCRMTable/types/shared";

export function useDetailModalTabs<T extends BaseEntity>({
  item,
  entityName,
}: {
  item: T | undefined;
  entityName: string;
}) {
  // Get entity-specific tabs - MUST call all hooks at top level (Rules of Hooks)
  const contactTabs = useContactDetailModalTabs();
  const ticketTabs = useTicketDetailModalTabs();
  const taskTabs = useTaskDetailModalTabs();
  const companyTabs = useCompanyDetailModalTabs();
  const timeEntryTabs = useTimeEntryDetailModalTabs();
  const dealTabs = useDealDetailModalTabs();
  const communicationsTabs = useCommunicationsDetailModalTabs();
  const offeringTabs = useOfferingDetailModalTabs();
  const recurringTransactionTabs = useRecurringTransactionDetailModalTabs();
  const contractTabs = useContractDetailModalTabs();
  const adminUserTabs = useAdminUserDetailModalTabs();

  // Select the appropriate tabs config based on entity type
  const entityTabsConfig = useMemo(() => {
    if (!item) return null;

    switch (entityName) {
      case "contacts":
        return contactTabs;
      case "tickets":
        return ticketTabs;
      case "tasks":
        return taskTabs;
      case "companies":
        return companyTabs;
      case "time_entries":
        return timeEntryTabs;
      case "deals":
        return dealTabs;
      case "communications":
        return communicationsTabs;
      case "offerings":
        return offeringTabs;
      case "recurring-transactions":
        return recurringTransactionTabs;
      case "contracts":
        return contractTabs;
      case "admin_users":
        return adminUserTabs;
      default:
        // Default: no tabs
        return null;
    }
  }, [
    item,
    entityName,
    contactTabs,
    ticketTabs,
    taskTabs,
    companyTabs,
    timeEntryTabs,
    dealTabs,
    communicationsTabs,
    offeringTabs,
    recurringTransactionTabs,
    contractTabs,
    adminUserTabs,
  ]);

  // Manage active tab state
  const [activeTab, setActiveTab] = useState(() => {
    return entityTabsConfig?.tabs[0]?.id || "details";
  });

  useEffect(() => {
    if (!entityTabsConfig?.tabs?.length) return;
    setActiveTab((prev) => {
      if (prev && entityTabsConfig.tabs.some((t) => t.id === prev)) {
        return prev;
      }
      return entityTabsConfig.tabs[0]?.id || "details";
    });
  }, [entityTabsConfig]);

  return {
    entityTabsConfig,
    activeTab,
    setActiveTab,
  };
}
